import { isAdmin, isEmployee, isManager, isManagerOrAdmin, isSuperAdmin } from './userTypes';
import {
  canViewOtherUser,
  hasAnyAccessPermission,
  isViewingSelf,
  isEmployeeManagedByUser,
  canAdminStartRoleChange,
} from './utils';
import {
  PERMISSION_NO_ACCESS,
  PERMISSION_VIEW_AND_EDIT,
  PERMISSION_VIEW_ONLY,
  PLATFORM_CALENDAR,
  PLATFORM_PEOPLE,
  PLATFORM_UPDATES,
  PLATFORM_TIME_OFF,
  OLD_TIME_OFF,
} from './constants';

const hasPlatformVisibility = (user) => Boolean(user?.userRole?.permissions?.platformVisibility);

const hasOwnPlatformVisibility = (user) => Boolean(user?.userRole?.permissions?.ownPlatformVisibility);

export const canViewPlatform = (area, user) =>
  hasAnyAccessPermission(user.userRole?.permissions?.platformVisibility[area]);

export const canViewOwnPlatform = (area, user) =>
  hasAnyAccessPermission(user.userRole?.permissions?.ownPlatformVisibility[area]);

export const canEditPlatform = (area, user) => {
  const permission = area !== PLATFORM_CALENDAR ? PERMISSION_VIEW_AND_EDIT : PERMISSION_VIEW_ONLY;
  return user.userRole?.permissions?.platformVisibility[area] === permission;
};

export const canEditOwnPlatform = (area, user) => {
  const permission = area !== PLATFORM_CALENDAR ? PERMISSION_VIEW_AND_EDIT : PERMISSION_VIEW_ONLY;
  return user.userRole?.permissions?.ownPlatformVisibility[area] === permission;
};

export const canViewOtherUserPlatform = (user, employee) =>
  canViewOtherUser(user, employee) && hasPlatformVisibility(user);

export const canViewPeopleArea = (user) => {
  if (isSuperAdmin(user)) return true;
  // Manager uses own platform permissions rather platform
  return isManager(user) ? canViewOwnPlatform(PLATFORM_PEOPLE, user) : canViewPlatform(PLATFORM_PEOPLE, user);
};

// Updates is null on non-default roles so check that it just doesn't equal no access
const canViewUpdatesArea = (user, employee) => {
  const hasPermission = user.userRole?.permissions?.platformVisibility.updates !== PERMISSION_NO_ACCESS;
  if (!isViewingSelf(user, employee)) {
    return canViewOtherUserPlatform(user, employee) && hasPermission;
  }
  return hasPermission;
};

const isPlatformOptionEnabled = (option, user) => {
  return Boolean(user.userRole?.permissions?.platformVisibility[option]);
};

const canEditPeopleArea = (user) => {
  if (isSuperAdmin(user)) return true;
  return isAdmin(user) && canEditPlatform(PLATFORM_PEOPLE, user);
};

export const canPerformPlatformTask = (task, user, employee) => {
  return canViewOtherUserPlatform(user, employee) && isPlatformOptionEnabled(task, user);
};

export const canViewUserTimeOffs = (user, employee) => {
  //If Admin is a user's manager, give view and edit access regardless of the LDE's he holds
  return isAdmin(user) && isEmployeeManagedByUser(user, employee.id);
};

const canViewPeopleOrUpdatesArea = (area, user, employee) => {
  if (area === PLATFORM_PEOPLE) {
    return canViewPeopleArea(user);
  }

  if (area === PLATFORM_UPDATES) {
    return canViewUpdatesArea(user, employee);
  }
};

const employeeHasPlatformVisibility = (user) => isEmployee(user) && hasPlatformVisibility(user);

const managerHasPlatformVisibility = (user) => isManagerOrAdmin(user) && hasOwnPlatformVisibility(user);

export const canViewPlatformArea = (area, user, employee = null, tabIdentifier = null) => {
  if (isSuperAdmin(user)) return true;
  if ([PLATFORM_PEOPLE, PLATFORM_UPDATES].includes(area)) return canViewPeopleOrUpdatesArea(area, user, employee);
  // If viewing self, no employee or employee id is same id
  if (isViewingSelf(user, employee)) {
    if (employeeHasPlatformVisibility(user)) {
      // Employees only have platform visibility so this is used rather than own platform visibility
      return canViewPlatform(area, user);
    }
    if (managerHasPlatformVisibility(user)) {
      // Managers and Admins use own platform visibility
      return canViewOwnPlatform(area, user);
    }
  }
  // This is added for Time off tab only
  // For context, please check CT-3421
  if (area === PLATFORM_TIME_OFF && tabIdentifier === OLD_TIME_OFF && canViewUserTimeOffs(user, employee)) return true;
  if (canViewOtherUserPlatform(user, employee)) {
    // Managers and Admins looking at other users must have relation to user and use platform visibility
    return canViewPlatform(area, user);
  }
  return false;
};

export const canEditPlatformArea = (area, loggedInUser, employee = null) => {
  if (isSuperAdmin(loggedInUser)) return true;

  if (area === PLATFORM_PEOPLE) {
    return canEditPeopleArea(loggedInUser);
  }

  if (employee === null || loggedInUser.id === employee?.id) {
    if (isEmployee(loggedInUser) && hasPlatformVisibility(loggedInUser)) {
      // Employees only have platform visibility so this is used rather than own platform visibility
      return canEditPlatform(area, loggedInUser);
    }
    if (isManagerOrAdmin(loggedInUser) && hasOwnPlatformVisibility(loggedInUser)) {
      // Managers and Admins use own platform visibility
      return canEditOwnPlatform(area, loggedInUser);
    }
  }
  if (canViewOtherUserPlatform(loggedInUser, employee)) {
    // Managers and Admins looking at other users must have relation to user and use platform visibility
    return canEditPlatform(area, loggedInUser);
  }
  return false;
};

export const canStartRoleChange = (user) => {
  return (
    isSuperAdmin(user) ||
    (isAdmin(user) && user.userRole.permissions.platformVisibility.canChangeUserRole) ||
    (isManager(user) && user.userRole.permissions.otherRoleVisibility.canChangeUserRole)
  );
};

export const checkAdminPermissionLevels = (user, employee) => {
  if (isManager(user) || isSuperAdmin(user) || user.id === employee.id) {
    return true;
  }
  return Boolean(
    (user.userRole?.locationPermissionLevel?.includes('all') ||
      user.userRole?.locationPermissionLevel?.includes(String(employee.locationId))) &&
      (user.userRole?.teamPermissionLevel?.includes('all') ||
        user.userRole?.teamPermissionLevel?.includes(String(employee.teamId))) &&
      (user.userRole?.statusPermissionLevel?.includes('all') ||
        user.userRole?.statusPermissionLevel?.includes(employee.employeeType)),
  );
};

export const canViewUserTasks = (user, employee) => {
  if (isSuperAdmin(user)) return true;
  if (checkAdminPermissionLevels(user, employee)) {
    if (user.id === employee.id) {
      return canViewOwnPlatform('task', user);
    }
    return canViewPlatform('task', user);
  }
  return false;
};

export const canstartAndEditRoleChange = (user, userBeingViewed) => {
  return (
    isSuperAdmin(user) ||
    ((user.userRole.permissions.otherRoleVisibility.canChangeUserRole ||
      user.userRole.permissions.platformVisibility.canChangeUserRole) &&
      ((isEmployeeManagedByUser(user, userBeingViewed?.id) && !isSuperAdmin(userBeingViewed)) ||
        (isAdmin(user) && canAdminStartRoleChange(user, userBeingViewed))))
  );
};
